/**
 * DroneBroadcaster — record camera to WebM chunks, POST to backend.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { toast } from "sonner";
import TopNav from "@/components/TopNav";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Loader2, Camera, Copy, ArrowLeft, Play, StopCircle, Monitor, MicOff, CheckCircle2 } from "lucide-react";

const API = (() => {
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return "http://localhost:8001/api";
  return `https://api.${host}/api`;
})();

const getHeaders = () => {
  const t = localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function DroneBroadcaster() {
  const auth = useAuth();
  const navigate = useNavigate();
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const seqRef = useRef(0);
  const bcIdRef = useRef(null);
  const videoCallbackRef = useRef(null);

  const [cameras, setCameras] = useState([]);
  const [mics, setMics] = useState([]);
  const [camId, setCamId] = useState("");
  const [micId, setMicId] = useState("");
  const [title, setTitle] = useState("");
  const [status, setStatus] = useState("idle");
  const [streamName, setStreamName] = useState(null);
  const [error, setError] = useState("");
  const [hasVideo, setHasVideo] = useState(false);

  // Use callback ref to always set srcObject when video mounts
  const videoRef = useCallback(node => {
    videoCallbackRef.current = node;
    if (node && streamRef.current) {
      node.srcObject = streamRef.current;
    }
  }, []);

  useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then(all => {
      setCameras(all.filter(d => d.kind === "videoinput"));
      setMics(all.filter(d => d.kind === "audioinput"));
    }).catch(() => {});
    return () => { stopAll(); };
  }, []);

  const startPreview = async () => {
    try {
      stopMedia();
      const s = await navigator.mediaDevices.getUserMedia({
        video: camId ? { deviceId: { exact: camId } } : true,
        audio: micId ? { deviceId: { exact: micId } } : true,
      });
      streamRef.current = s;
      // Set on video element if it exists
      if (videoCallbackRef.current) videoCallbackRef.current.srcObject = s;
      setHasVideo(true);
      setStatus("preview");
    } catch { setError("Camera/mic access denied"); toast.error("Camera access required"); }
  };

  const stopMedia = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    setHasVideo(false);
  };

  const goLive = async () => {
    if (!streamRef.current) await startPreview();
    if (!streamRef.current) return;

    const id = "bc_" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    bcIdRef.current = id;
    setStreamName(id);
    setStatus("live");
    seqRef.current = 0;

    const mime = "video/webm;codecs=vp8,opus";

    try {
      const r = new MediaRecorder(streamRef.current, { mimeType: mime, videoBitsPerSecond: 500000 });
      recorderRef.current = r;

      r.ondataavailable = async (e) => {
        if (!e.data || e.data.size < 500) return;
        const seq = seqRef.current++;
        try {
          await fetch(API + "/broadcast/" + id + "/chunk", {
            method: "POST",
            headers: { ...getHeaders(), "Content-Type": "application/octet-stream" },
            body: e.data,
          });
        } catch {}
      };

      r.start(2000);
      toast.success("Broadcasting live!");
    } catch (e) {
      setError("Recording not supported in this browser");
      setStatus("idle");
    }
  };

  const stopBroadcast = async () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
    stopMedia();
    setStatus("stopped");
    toast.success("Broadcast ended");
  };

  const stopAll = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
    stopMedia();
  };

  const copyUrl = () => {
    const url = window.location.origin + "/drones/stream/" + streamName;
    navigator.clipboard.writeText(url); toast.success("URL copied!");
  };

  return (
    <div className="min-h-screen bg-app text-app">
      <TopNav />
      <div className="max-w-4xl mx-auto px-3 py-4">
        <button onClick={() => navigate("/drones")} className="flex items-center gap-1 text-app-secondary hover:text-app text-xs mb-4"><ArrowLeft className="w-3.5 h-3.5" /> Back</button>
        <h1 className="text-lg font-bold mb-4">Live Broadcast</h1>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-black border-app-secondary/20 overflow-hidden">
            <div className="aspect-video bg-slate-900 flex items-center justify-center relative">
              {hasVideo ? (
                <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover" />
              ) : (
                <div className="text-center"><Camera className="w-10 h-10 text-slate-700 mx-auto mb-2" /><p className="text-xs text-slate-600">Camera preview</p></div>
              )}
              {status === "live" && <div className="absolute top-2 left-2 bg-red-600 px-2 py-0.5 rounded-full flex items-center gap-1"><span className="w-2 h-2 bg-white rounded-full animate-pulse" /><span className="text-[10px] text-white font-semibold">LIVE</span></div>}
            </div>
          </Card>

          <div className="space-y-3">
            <div>
              <label className="text-[10px] text-app-secondary uppercase font-semibold mb-1 block">Title</label>
              <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="My Broadcast" maxLength={80}
                className="bg-surface-item border-input text-app text-xs h-8" disabled={status === "live"} />
            </div>
            <div>
              <label className="text-[10px] text-app-secondary uppercase font-semibold mb-1 block">Camera</label>
              <select value={camId} onChange={e => { setCamId(e.target.value); }} className="w-full bg-surface-item border border-input rounded px-2 py-1.5 text-xs text-app" disabled={status === "live"}>
                {cameras.map(d => <option key={d.deviceId} value={d.deviceId}>{d.label || "Camera"}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] text-app-secondary uppercase font-semibold mb-1 block">Microphone</label>
              <select value={micId} onChange={e => { setMicId(e.target.value); }} className="w-full bg-surface-item border border-input rounded px-2 py-1.5 text-xs text-app" disabled={status === "live"}>
                {mics.map(d => <option key={d.deviceId} value={d.deviceId}>{d.label || "Mic"}</option>)}
              </select>
            </div>
            {error && <p className="text-xs text-red-400 bg-red-600/10 px-2 py-1 rounded">{error}</p>}
            <div className="flex gap-2 pt-2">
              {status === "idle" && <Button onClick={startPreview} className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white text-xs h-9 rounded-lg"><Monitor className="w-3.5 h-3.5 mr-1" /> Preview</Button>}
              {status === "preview" && <Button onClick={goLive} className="flex-1 bg-red-600 hover:bg-red-500 text-white text-xs h-9 rounded-lg font-semibold"><Play className="w-3.5 h-3.5 mr-1" /> Go Live</Button>}
              {status === "live" && <Button onClick={stopBroadcast} className="flex-1 bg-red-700 hover:bg-red-600 text-white text-xs h-9 rounded-lg font-semibold"><StopCircle className="w-3.5 h-3.5 mr-1" /> Stop</Button>}
            </div>
          </div>
        </div>

        {status === "live" && streamName && (
          <Card className="bg-emerald-600/10 border-emerald-600/30 mt-4 p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /><span className="text-xs text-emerald-300 font-medium">Share:</span></div>
              <Button onClick={copyUrl} className="bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] px-2 py-1 h-auto rounded flex items-center gap-1"><Copy className="w-3 h-3" /> Copy Link</Button>
            </div>
            <p className="text-xs text-app-secondary mt-1 break-all select-all bg-black/20 rounded px-2 py-1 font-mono">{window.location.origin}/drones/stream/{streamName}</p>
          </Card>
        )}
      </div>
    </div>
  );
}
